import { useCallback, useRef } from 'react';

import { useConfig } from '../../contexts/config-context';

import type { CommonAddress } from './atlas-service';
import { extractJapanPostalCode } from './postal-code';
import { useAtlas } from './use-atlas';

type UseJapanAutocompleteParams = {
  isAutocompleteEnabled: boolean;
};

export function useJapanAutocomplete({
  isAutocompleteEnabled,
}: UseJapanAutocompleteParams) {
  const state = useRef<{
    postalCode?: string;
    address?: CommonAddress;
    hasAddress: boolean;
  }>({ hasAddress: false });
  const { instance, language, atlasBaseUrl } = useConfig();
  const atlas = useAtlas({
    enabled: isAutocompleteEnabled,
    getInstance: () => instance ?? '',
    language,
    atlasBaseUrl,
  });

  const searchByPostalCode = useCallback(
    async (text: string) => {
      const postalCode = extractJapanPostalCode(text);
      if (!postalCode) {
        return;
      }

      if (state.current.postalCode === postalCode && state.current.hasAddress) {
        return state.current.address;
      }

      state.current = { postalCode, address: undefined, hasAddress: false };

      try {
        const address = await atlas?.searchByPostalCode({
          countryCode: 'JP',
          postalCode,
        });

        state.current.hasAddress = true;
        state.current.address = undefined;

        if (address?.formattedAddress) {
          state.current.address = address;
          return address;
        }
        return;
      } catch (e) {
        console.error(e);
      }

      return;
    },
    [atlas],
  );

  return { searchByPostalCode };
}
